import { useRef, useState } from "react";
import styled from "styled-components";
import { useClickOutside } from "@utils/hooks";
import Down from "./icons/Down";

interface Props<O, V> {
	options: O[];
	/**
	 * Maps option objects value.
	 * For string options simply set it to:
	 * `(opt) => opt`
	 */
	optionToValue: (option: O) => V;
	/**
	 * Maps option object to label.
	 * For string options simply set it to:
	 * `(opt) => opt`
	 */
	optionToLabel: (option: O) => string;
	/**
	 * Predicate to check if value is option
	 */
	selectedOptionPredicate: (value: V[], option: O) => boolean;
	className?: string;
	onChange: (value: V[], index: number) => any;
	value: V[];
	prefix?: string;
	multiSelect?: boolean;
	noSelection?: string;
}

export function Dropdown<O, V>({
	onChange,
	value,
	options,
	className,
	prefix,
	noSelection,
	multiSelect,
	optionToValue,
	selectedOptionPredicate,
	optionToLabel,
}: Props<O, V>) {
	const [open, setOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useClickOutside(dropdownRef, () => {
		setOpen(false);
	});

	function handleChange(selectedValue: V, index: number) {
		if (!multiSelect) {
			setOpen(false);

			onChange([selectedValue], index);
		} else {
			const newVal = value?.includes(selectedValue)
				? value?.filter((v) => v !== selectedValue)
				: [...value, selectedValue];
			onChange(newVal, index);
		}
	}

	const selectedOption = options.find((opt) => selectedOptionPredicate(value, opt));

	const valueLabel = selectedOption ? optionToLabel(selectedOption) : noSelection || "";

	return (
		<Styles className={`${className || ""} ${open ? "dropdown-open" : ""}`} ref={dropdownRef}>
			<div className="selection" onClick={() => setOpen(!open)}>
				<div>
					<span>{prefix || ""}</span>
					{valueLabel}
				</div>
				<Down size={12} />
			</div>
			{open && (
				<DropdownPopover>
					{options.map((o, i) => {
						const mappedValue = optionToValue(o);
						const mappedLabel = optionToLabel(o);
						return (
							<div
								key={mappedValue + ""}
								onClick={() => handleChange(mappedValue, i)}
								className={`dropdown-option ${value?.includes(mappedValue) ? "selected-option" : ""} `}
							>
								{multiSelect && <input type="checkbox" checked={value?.includes(mappedValue)} />}
								{mappedLabel}
							</div>
						);
					})}
				</DropdownPopover>
			)}
		</Styles>
	);
}

const DropdownPopover = styled.div`
	position: absolute;
	top: 3rem;
	left: 0;
	z-index: 10;
	background: white;
	border: 1px solid ${(props) => props.theme.colors.dividerLight};
	border-radius: 5px;
	padding: 0.5rem;
	min-width: 300px;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

	.dropdown-option {
		display: flex;
		gap: 0.5rem;
		align-items: center;
		cursor: pointer;
		padding: 0.5rem;
		border-radius: 3px;

		&:hover {
			background-color: ${(props) => props.theme.colors.dividerLight};
		}

		&.selected-option {
			font-weight: 500;
		}
	}
`;

const Styles = styled.div`
	position: relative;
	user-select: none;
	border: 1px solid ${(props) => props.theme.colors.dividerLight};
	border-radius: 5px;
	padding: 0.5rem;

	.selection {
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.5rem;

		&:hover {
			opacity: 0.7;
		}

		span {
			font-weight: 500;
		}

		svg {
			transition: transform 0.125s;
		}
	}

	&.dropdown-open {
		svg {
			transform: rotate(-180deg);
		}
	}
`;
